/**
 * Quick Links Component
 * Refactored for best practices:
 * - CSS-driven layout and state
 * - No inline style manipulations
 * - Centralized config for Flickity
 */

(function () {
  "use strict";

  const CONFIG = {
    flickityOptions: {
      contain: true,
      pageDots: false,
      prevNextButtons: true,
      groupCells: false,
      cellAlign: "left",
      accessibility: true,
      setGallerySize: true,
      dragThreshold: 10,
      freeScroll: false,
    },
    carouselMediaQuery: "(min-width: 769px)",
    selectors: {
      wrapper: ".quick-links__wrapper",
      list: ".quick-links__list",
      link: ".quick-link__link",
    },
    classes: {
      loading: "loading",
      hasCarousel: "has-carousel",
      noCarousel: "no-carousel",
    },
  };

  function debounce(fn, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function initializeCarousel(listEl, wrapperEl) {
    if (typeof Flickity === "undefined") {
      console.warn("Flickity not loaded, falling back to static list");
      finalizeStatic(listEl, wrapperEl);
      return null;
    }
    try {
      const flkty = new Flickity(listEl, CONFIG.flickityOptions);
      listEl.classList.remove(CONFIG.classes.loading);
      wrapperEl.classList.add(CONFIG.classes.hasCarousel);
      wrapperEl.classList.remove(CONFIG.classes.noCarousel);
      return flkty;
    } catch (err) {
      console.error("Flickity init error:", err);
      finalizeStatic(listEl, wrapperEl);
      return null;
    }
  }

  function finalizeStatic(listEl, wrapperEl) {
    listEl.classList.remove(CONFIG.classes.loading);
    wrapperEl.classList.add(CONFIG.classes.noCarousel);
    wrapperEl.classList.remove(CONFIG.classes.hasCarousel);
  }

  function shouldUseCarousel(listEl) {
    const meetsWidth = window.matchMedia(CONFIG.carouselMediaQuery).matches;
    if (!meetsWidth) {
      return false; // keep static layout on narrow viewports to avoid iOS reflows
    }
    // Add a 1px buffer to account for fractional widths that can fluctuate on iOS
    return listEl.scrollWidth > listEl.clientWidth + 1;
  }

  function setupLinks(listEl) {
    listEl.classList.add(CONFIG.classes.loading);
    const links = listEl.querySelectorAll(CONFIG.selectors.link);
    links.forEach((link, idx) => {
      link.addEventListener("keydown", (e) => {
        let target;
        switch (e.key) {
          case "ArrowRight":
            target = (idx + 1) % links.length;
            links[target].focus();
            e.preventDefault();
            break;
          case "ArrowLeft":
            target = (idx - 1 + links.length) % links.length;
            links[target].focus();
            e.preventDefault();
            break;
          case "Home":
            links[0].focus();
            e.preventDefault();
            break;
          case "End":
            links[links.length - 1].focus();
            e.preventDefault();
            break;
        }
      });
    });
  }

  function init() {
    document.querySelectorAll(CONFIG.selectors.wrapper).forEach((wrapper) => {
      const list = wrapper.querySelector(CONFIG.selectors.list);
      if (!list) return;
      setupLinks(list);

      let flkty = null;
      if (shouldUseCarousel(list)) {
        flkty = initializeCarousel(list, wrapper);
      } else {
        finalizeStatic(list, wrapper);
      }

      window.addEventListener(
        "resize",
        debounce(() => {
          const needsCarousel = shouldUseCarousel(list);
          if (needsCarousel && !flkty) {
            flkty = initializeCarousel(list, wrapper);
          } else if (!needsCarousel && flkty) {
            flkty.destroy();
            flkty = null;
            finalizeStatic(list, wrapper);
          }
        }, 150)
      );
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
})();
